import { lazy } from 'react';
import { createBrowserRouter, Navigate, redirect } from 'react-router-dom';

import Loadable from 'components/Loadable';
import SimpleLayout from 'layout/Simple';
import DashboardLayout from 'layout/Dashboard';
import { SimpleLayoutType } from 'config';
import UserDashboardLayout from '../layout/UserDashboard';
import BillingResult from 'billing/Result';

// auth wrappers (user)
import RequireAuth from './RequireAuth';
import AlreadyAuthed from './AlreadyAuthed';

import OAuthRelay from 'login/OAuthRelay';

// admin wrappers (separate token)
import RequireAdminAuth from './RequireAdminAuth';
import AlreadyAuthedAdmin from './AlreadyAuthedAdmin';
import ErrorBoundary from './ErrorBoundary';

// pages (reuse or split admin/user pages as needed)
const LoginPage = Loadable(lazy(() => import('login/index')));
const DashboardPage = Loadable(lazy(() => import('Dashboard/dashboard')));
const Subscriptions = Loadable(lazy(() => import('../subscriptions')));
const RazorpayReturnPage = Loadable(lazy(() => import('../billing/RazorpayReturnPage')));
const Addons = Loadable(lazy(() => import('../subscriptions/addons')));
const FacebookUsers = Loadable(lazy(() => import('../facebook-users')));
const Invoices = Loadable(lazy(() => import('../invoice')));
const AddOnInvoices = Loadable(lazy(() => import('../invoice/addonsInvoice')));
const UserLogin = Loadable(lazy(() => import('login/UserFacebookLogin')));
const UserDashboard = Loadable(lazy(() => import('Dashboard/dashboard')));
const BulkUsers = Loadable(lazy(() => import('../Bulk list')));
const AdminProfileView = Loadable(
  lazy(() => import('profile/AdminProfileView'))
);
const UserProfile = Loadable(lazy(() => import('profile/Userprofile'))); // src/userprofile/index.jsx
const FacebookPages = Loadable(lazy(() => import('facebook/pages'))); // src/facebook/pages/index.jsx
const PageView = Loadable(lazy(() => import('facebook/pages/view/')));
const BillingSuccess = Loadable(lazy(() => import('billing/Success')));
const BillingCancel = Loadable(lazy(() => import('billing/Cancel')));
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));


// (optional) admin-specific pages; for now reuse the same ones
const AdminLoginPage = LoginPage;
const AdminDashboardPage = DashboardPage;

// Intercept Razorpay's POST callbacks to extract form data and redirect cleanly to the GET handler
const handleRazorpayPost = async ({ request }) => {
  if (request.method === "POST") {
    try {
      const formData = await request.formData();
      const url = new URL(request.url);
      for (const [key, value] of formData.entries()) {
        url.searchParams.set(key, value);
      }
      return redirect(url.pathname + url.search);
    } catch (e) {
      return null;
    }
  }
  return null;
};

const router = createBrowserRouter(
  [
    // root -> /login (user)
    { index: true, element: <LandingPage />, errorElement: <ErrorBoundary /> },
    { path: '/auth/facebook/callback', element: <OAuthRelay /> },
    { path: '/auth/facebook/callback/', element: <OAuthRelay /> },
    { path: '/oauth-relay', element: <OAuthRelay /> },
    { path: '/oauth-relay/', element: <OAuthRelay /> },

    // ── Razorpay return pages ──────────────────────────────────────────────
    // These MUST be top-level (outside RequireAuth & UserDashboardLayout) so
    // that after a full-page reload triggered by a Razorpay redirect:
    //   • No auth-gate can steal the redirect before the page reads URL params
    //   • No sidebar/header wraps the full-screen verification UI
    // The component itself reads the JWT from localStorage/sessionStorage and
    // calls the verify API directly.
    //
    // The `action: handleRazorpayPost` is kept as a safety net for any
    // POST-based redirect edge-cases, though the primary flow now uses the
    // Razorpay `handler` callback (GET params) for subscriptions and the
    // payment-link GET redirect for add-ons.
    { path: '/billing/return',          element: <RazorpayReturnPage />, action: handleRazorpayPost },
    { path: '/billing/razorpay-return', element: <RazorpayReturnPage />, action: handleRazorpayPost },
    { path: '/billing/addon-return',    element: <RazorpayReturnPage />, action: handleRazorpayPost },

    // ================= USER APP =================
    {
      path: '/login',
      element: (
        <AlreadyAuthed>
          <SimpleLayout layout={SimpleLayoutType.SIMPLE} />
        </AlreadyAuthed>
      ),
      errorElement: <ErrorBoundary />,
      children: [{ index: true, element: <UserLogin /> }]
    },
    {
      path: '/',
      element: (
        <RequireAuth>
          <UserDashboardLayout />
        </RequireAuth>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        { path: 'dashboard', element: <FacebookPages /> },
        { path: 'profile', element: <UserProfile /> },
        { path: 'pages/:pageId', element: <PageView /> },
        { path: 'billing/success', element: <BillingResult mode="success" /> },
        { path: 'billing/cancel', element: <BillingResult mode="cancel" /> },
      ]
    },

    // ================= ADMIN APP =================
    // /admin -> /admin/login

    {
      path: '/admin',
      children: [
        {
          path: 'login',
          element: (
            <AlreadyAuthedAdmin>
              <SimpleLayout layout={SimpleLayoutType.SIMPLE} />
            </AlreadyAuthedAdmin>
          ),
          children: [{ index: true, element: <LoginPage /> }]
        },
        {
          path: '',
          element: (
            <RequireAdminAuth>
              <DashboardLayout />
            </RequireAdminAuth>
          ),
          children: [
            { index: true, element: <Navigate to="dashboard" replace /> },
            { path: 'dashboard', element: <DashboardPage /> },
            { path: 'subscriptions/manage', element: <Subscriptions /> },
            { path: 'subscriptions/addons', element: <Addons /> },
            { path: 'facebook-users', element: <FacebookUsers /> },
            { path: 'invoices', element: <Invoices /> },
            { path: 'profile', element: <AdminProfileView /> },
            { path: 'bulk-jobs', element: <BulkUsers /> },
          ]
        }
      ]
    }
  ],
  { basename: import.meta.env.VITE_APP_BASE_NAME } // '/zingoblast'
);

export default router;
